import { Card, CardStatusEnum, CardTypeEnum } from '@varo/graphql-schema-types';
import { Href, NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { CardExpirationStatusEnum } from 'features/cardManagement/helpers/CardExpirationStatusEnum';
import { isCardAlreadyExpired } from 'features/cardManagement/helpers/cardExpirationValidation.helpers';
import { useCardSelectionCardLockExperience } from 'features/cardManagement/hooks/useCardLockExperience/useCardLockExperience';
import { useFetchCardDetails } from 'features/cardManagement/hooks/useFetchCardDetails';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ImageRequireSource, StyleSheet, View } from 'react-native';
import ProductOfferCard from 'shared/components/vxl/cards/ProductOfferCard/ProductOfferCard';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { TagProps, TagType } from 'shared/components/vxl/Tag/Tag';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const DEBIT_CARD_TEST_ID_STATUS_SUFFIXES: Record<CardStatusEnum, string> = {
  [CardStatusEnum.Unknown]: 'unknown',
  [CardStatusEnum.New]: 'new',
  [CardStatusEnum.Activated]: 'activated',
  [CardStatusEnum.NotActivated]: 'not-activated',
  [CardStatusEnum.Locked]: 'locked',
  [CardStatusEnum.LostStolenBroken]: 'lost-stolen-broken',
  [CardStatusEnum.Suspended]: 'suspended',
  [CardStatusEnum.Canceled]: 'canceled',
};
const MISSING_CARD_STATUS_TEST_ID_SUFFIX = 'missing-status';
const UNMAPPED_CARD_STATUS_TEST_ID_SUFFIX = 'unmapped-status';

const getCardSelectionTestID = (card: Card, cards: Card[]) => {
  if (card.type !== CardTypeEnum.DebitCard) {
    return undefined;
  }

  const statusSuffix =
    card.status === undefined || card.status === null
      ? MISSING_CARD_STATUS_TEST_ID_SUFFIX
      : (DEBIT_CARD_TEST_ID_STATUS_SUFFIXES[card.status] ??
        UNMAPPED_CARD_STATUS_TEST_ID_SUFFIX);
  const hasSameStatusDebitCard = cards.some(
    (candidate) =>
      candidate.id !== card.id &&
      candidate.type === CardTypeEnum.DebitCard &&
      candidate.status === card.status,
  );
  const duplicateCardSuffix = hasSameStatusDebitCard ? `-card-${card.id}` : '';

  return `card-selection-debit-card-${statusSuffix}${duplicateCardSuffix}-button`;
};

/* eslint-disable  @typescript-eslint/no-require-imports */
const believeCard =
  require('assets/images/vxl/cards/believe-card-a.png') as ImageRequireSource;

const debitCard =
  require('assets/images/vxl/cards/debit-card-a.png') as ImageRequireSource;
/* eslint-enable  @typescript-eslint/no-require-imports */

const CardSelectionScreen = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('cardManagement', {
    keyPrefix: 'cardSelection',
  });

  const { tag: lockTag } = useCardSelectionCardLockExperience();

  const { sourceData, fetching, error } = useFetchCardDetails();

  const getTagProps = (card: Card): TagProps => {
    if (
      isCardAlreadyExpired(card.expiration) === CardExpirationStatusEnum.Expired
    ) {
      return {
        text: translatedText('tag.expired'),
        type: TagType.ERROR,
        iconKey: '',
      };
    } else if (card.isLocked) {
      return lockTag;
    } else if (card.status === CardStatusEnum.New) {
      return {
        text: translatedText('tag.processing'),
        type: TagType.INFO,
        iconKey: '',
      };
    } else if (card.status === CardStatusEnum.Suspended) {
      return {
        text: translatedText('tag.suspended'),
        type: TagType.ERROR,
        iconKey: '',
      };
    } else if (!card.isPinSet) {
      return {
        text: translatedText('tag.createPin'),
        type: TagType.WARNING,
        iconKey: '',
      };
    }

    return {
      text: '',
      type: TagType.INFO_REVERSE,
      iconKey: '',
    };
  };

  const errorResponse = error;
  const cards = sourceData?.cards ?? [];

  const hasDebitCard = cards.some(
    (card) => card.type === CardTypeEnum.DebitCard,
  );

  const navigateToHelpChat = () => {
    router.push('/myvaro/help/chat' as Href);
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: 'cardManagement',
  });

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: translatedText('title'),
          headerShown: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
          headerBackVisible: true,
        }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorResponse ? (
            <ErrorScreen
              screenType={ErrorScreenType.Warning}
              messageHeadline={translatedText('error.noCards')}
              primaryButton={{
                title: translatedText('buttons.done'),
                onPress: () => router.back(),
              }}
            />
          ) : null
        }
      />

      {cards.map((item) => {
        const { text, type, iconKey } = getTagProps(item);
        return (
          <React.Fragment key={item.id}>
            <ListItem
              testID={getCardSelectionTestID(item, cards)}
              leadingAccessory={{
                id: item.id,
                name: VXLComponentId.ICON_AVATAR,
                props: {
                  imageSource:
                    item.type === CardTypeEnum.ChargeCard
                      ? believeCard
                      : debitCard,
                  imageStyle: {
                    height: 36,
                    width: 36,
                  },
                  resizeMode: 'contain',
                  variant: 'MD',
                },
              }}
              primaryCopy={
                item.type === CardTypeEnum.ChargeCard
                  ? translatedText('cardTitle.varoBelieve')
                  : translatedText('cardTitle.varoDebit')
              }
              secondaryCopy={
                item.maskedCardNumber !== ''
                  ? `••••${item.maskedCardNumber}`
                  : ''
              }
              onPress={() => {
                router.push(`/myvaro/card/${item.id}` as Href);
              }}
              trailingAccessory={
                text !== ''
                  ? {
                      id: item.id,
                      name: VXLComponentId.TAG,
                      props: {
                        text: text,
                        type: type,
                        iconKey: iconKey,
                      },
                    }
                  : undefined
              }
            />
          </React.Fragment>
        );
      })}

      {!hasDebitCard && !fetching && !errorResponse ? (
        <View style={styles.productOfferContainer}>
          <ProductOfferCard
            titleText={translatedText('productOffer.title')}
            bodyText={translatedText('productOffer.message')}
            buttonText={translatedText('productOffer.buttonText')}
            illustrationKey="CARD_A"
            illustrationBackgroundColor={theme.color.expressive.orchid}
            onPress={navigateToHelpChat}
            analyticsId="orderNewDebitCardClicked"
            testID="productOfferCard"
            hasBorder
          />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  productOfferContainer: {
    margin: theme.spacing.m,
  },
});

export default CardSelectionScreen;
